import {
	getProductAvailableFromState,
	getSoftDeployAccessFromState,
	getFeatureAvailableFromState
} from 'App/components/hooks/featureHelper';
import { AccountSelf } from 'App/resources/AllIWant';
import { getDocumentTemplatesFromState } from 'Store/selectors/AppSelectors';
import store from 'Store/index';
import Client from 'App/resources/Model/Client';
import { IconName } from '@upsales/components';
import { openDrawer } from 'Services/Drawer';
import openModal from 'App/services/Modal';
import { Feature, Product } from 'Store/actions/FeatureHelperActions';
import { isAvailable, hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import { CreateRelationView } from 'App/babel/components/CreateRelation/CreateRelation';

import T from 'Components/Helpers/translate';
import { openEditAppointment } from 'Components/Modals/Appointment/EditAppointment';
import getAngularModule from 'App/babel/angularHelpers/getAngularModule';
import { SubaccountDrawerView } from 'App/babel/components/SubaccountDrawer/SubaccountDrawer';
import { openEditOrder } from 'App/components/EditOrder';
import openEditEsign from 'App/components/Esign/EditEsign/EditEsign';
import { openEditContact } from 'App/components/EditContact/openEditContact';
import AppService from 'App/legacy/AppService';

type Action = {
	id: string;
	visible?: (accountSelf: AccountSelf, client: Client) => boolean;
	onClick: () => void;
	icon: IconName;
	title: string;
};

export type CreateMenuAvailable = {
	contacts: boolean;
	activities: boolean;
	phonecalls: boolean;
	appointment: boolean;
	opportunities: boolean;
	orders: boolean;
	agreement: boolean;
	document: boolean;
	relations: boolean;
	market: boolean;
	assign: boolean;
	resetScore: boolean;
	files: boolean;
	esign: boolean;
	udo: boolean;
	support: boolean;
	target: boolean;
	projectPlan: boolean;
};

export const createMenuAvailable = (accountSelf: AccountSelf, client: Client): CreateMenuAvailable => {
	const hasMarketingEvents = isAvailable(Feature.MARKETING_EVENTS);
	return {
		contacts: getFeatureAvailableFromState(accountSelf, Feature.COMPANIES_AND_CONTACTS),
		activities: getFeatureAvailableFromState(accountSelf, Feature.ACTIVITIES_AND_APPOINTMENTS) && !!client.active,
		phonecalls: getFeatureAvailableFromState(accountSelf, Feature.ACTIVITIES_AND_APPOINTMENTS) && !!client.active,
		appointment: getFeatureAvailableFromState(accountSelf, Feature.ACTIVITIES_AND_APPOINTMENTS) && !!client.active,
		opportunities: getFeatureAvailableFromState(accountSelf, Feature.PIPELINE) && !!client.active,
		orders: getFeatureAvailableFromState(accountSelf, Feature.ORDERS) && !!client.active,
		agreement:
			getFeatureAvailableFromState(accountSelf, Feature.RECURRING_ORDER) &&
			AppService.getAccessRights().Agreement &&
			!!client.active,
		document: getFeatureAvailableFromState(accountSelf, Feature.DOCUMENTS),
		relations:
			getFeatureAvailableFromState(accountSelf, Feature.COMPANY_RELATIONS) &&
			!(getFeatureAvailableFromState(accountSelf, Feature.COMPANY_GROUP_CARD) && !!client.operationalAccount),
		// Uses the standalone isAvailable because MARKETING_EVENTS has a custom fn callback
		// that getFeatureAvailableFromState does not support (fn handling is commented out)
		market: hasMarketingEvents && !!client.active,
		assign: getFeatureAvailableFromState(accountSelf, Feature.LEADS) && !!client.active,
		resetScore: getProductAvailableFromState(accountSelf, Product.MA),
		files: getFeatureAvailableFromState(accountSelf, Feature.DOCUMENTS),
		esign: getFeatureAvailableFromState(accountSelf, Feature.ESIGN) && !!client.active,
		udo: getFeatureAvailableFromState(accountSelf, Feature.UDO) && !!client.active,
		support: getFeatureAvailableFromState(accountSelf, Feature.CUSTOMER_SUPPORT) && !!client.active,
		target: getFeatureAvailableFromState(accountSelf, Feature.CLIENT_TARGET) && !!client.active,
		projectPlan: getFeatureAvailableFromState(accountSelf, Feature.PROJECT_PLAN) && !!client.active
	};
};

export const createActions = (
	accountSelf: AccountSelf,
	client: Client,
	customerId: number,
	score?: number,
	contactsTotal?: number
): Action[] => {
	const hasSubaccountsV2 = getSoftDeployAccessFromState(accountSelf, 'SUB_ACCOUNTS_V2');
	const hasSubaccountsFeature = getFeatureAvailableFromState(accountSelf, Feature.SUB_ACCOUNTS) && hasSubaccountsV2;
	const hasCompanyGroupCardFeature = getFeatureAvailableFromState(accountSelf, Feature.COMPANY_GROUP_CARD);
	const hasSubScriptionModal = getSoftDeployAccessFromState(accountSelf, 'SUBSCRIPTION_MODAL');
	const hasEsign = !!AppService.getEsignIntegrations().length;
	const hasReactUploadFileModal = hasSoftDeployAccess('REACT_UPLOAD_FILE_MODAL');
	const UdoLink = getAngularModule('UdoLink');
	const documentTemplates = getDocumentTemplatesFromState(store.getState().App, 'client');
	const meta = AppService.getMetadata();

	const canCreateEntity = createMenuAvailable(accountSelf, client);

	return [
		{
			id: 'contacts',
			icon: 'user',
			title: T('clientCard.splitButton.contact'),
			visible: () => canCreateEntity.contacts && client.createRights.Contact,
			onClick: () => openEditContact({ account: client })
		},
		{
			id: 'activities',
			icon: 'activity',
			title: T('clientCard.splitButton.activity'),
			visible: () =>
				client.createRights.Activity &&
				canCreateEntity.activities &&
				!getSoftDeployAccessFromState(accountSelf, 'REMOVE_ACTIVITIES'),
			onClick: () => Tools.$upModal.open('editActivity', { activity: { client: { id: client.id } } })
		},
		{
			id: 'todo',
			icon: 'todo',
			title: T('clientCard.splitButton.todo'),
			visible: () =>
				client.createRights.Activity &&
				canCreateEntity.activities &&
				getSoftDeployAccessFromState(accountSelf, 'TODO_LIST'),
			onClick: () => openDrawer('CreateTodo', { client })
		},
		{
			id: 'phonecalls',
			icon: 'phone',
			title: T('clientCard.splitButton.phonecall'),
			visible: () =>
				client.createRights.Activity &&
				canCreateEntity.activities &&
				getSoftDeployAccessFromState(accountSelf, 'TODO_LIST'),
			onClick: () => openDrawer('CreateCall', { client })
		},
		{
			id: 'appointment',
			icon: 'calendar',
			title: T('clientCard.splitButton.appointment'),
			visible: () => client.createRights.Appointment && canCreateEntity.appointment,
			onClick: () => openEditAppointment({ appointment: { client: { id: client.id } } })
		},
		{
			id: 'opportunities',
			icon: 'opportunity',
			title: T('clientCard.splitButton.opportunity'),
			visible: () => client.createRights.Opportunity && canCreateEntity.opportunities,
			onClick: () => openEditOrder({ clientId: client.id, type: 'opportunity' })
		},
		{
			id: 'orders',
			icon: 'dollar',
			title: T('clientCard.splitButton.order'),
			visible: () => client.createRights.Order && canCreateEntity.orders,
			onClick: () => openEditOrder({ clientId: client.id })
		},
		{
			id: 'subscription',
			icon: 'recurring-order',
			title: T('clientCard.splitButton.subscription'),
			visible: () => client.createRights.Agreement && canCreateEntity.agreement && meta.params.AgreementEnabled,
			onClick: () => {
				if (!hasSubScriptionModal) {
					return Tools.$upModal.open('editAgreement', { accountId: client.id });
				}
				openModal('CreateSubscription', {
					client,
					createdFrom: 'accountAddButton',
					dontWait: true
				});
			}
		},
		...(meta.params.UserDefinedObject?.filter(udo => udo.link === UdoLink.ACCOUNT) || []).map(u => ({
			id: 'udo' + u.id,
			title: u.name,
			icon: 'puzzle-piece' as IconName,
			visible: () =>
				client.createRights[('UserDefObj' + u.id) as keyof typeof client.createRights] && canCreateEntity.udo,
			onClick: () => {
				openModal('EditUDO', {
					typeId: u.id,
					object: {
						client: { id: client.id, name: client.name }
					}
				});
			}
		})),
		{
			id: 'document',
			icon: 'file',
			title: T('clientCard.splitButton.document'),
			visible: () => client.userEditable && canCreateEntity.document && !!documentTemplates.length,
			onClick: () =>
				openModal('CreateDocumentModal', {
					type: 'account',
					id: client.id,
					templates: documentTemplates,
					accountId: client.id
				})
		},
		{
			id: 'relations',
			icon: 'sitemap',
			title: T('clientCard.splitButton.relations'),
			visible: () => client.userEditable && canCreateEntity.relations,
			onClick: () => {
				if (
					(hasCompanyGroupCardFeature || hasSubaccountsFeature) &&
					!client.operationalAccount &&
					!!client.active
				) {
					openDrawer('CreateRelation', { client, defaultView: CreateRelationView.Default });
				} else {
					openModal('AccountRelationModal', { account: client, customerId: customerId } as any);
				}
			}
		},
		{
			id: 'merge',
			icon: 'exchange',
			title: T('clientCard.splitButton.merge'),
			visible: () => client.userEditable,
			onClick: () => {
				openModal('MergeClients', {
					id: client.id,
					customerId: customerId,
					meta: { account: { data: client } }
				});
			}
		},
		{
			id: 'market',
			icon: 'tachometer',
			title: T('clientCard.splitButton.market'),
			visible: () => canCreateEntity.market,
			onClick: () => openModal('NewMarketEvent', { account: client })
		},
		{
			id: 'assign',
			icon: 'star',
			title: T('clientCard.splitButton.assign'),
			visible: () => client.userEditable && canCreateEntity.assign,
			onClick: () => openModal('AssignModal', { client: client })
		},
		{
			id: 'resetScore',
			icon: 'thumbs-down',
			title: T('clientCard.splitButton.reset'),
			visible: () => client.userEditable && canCreateEntity.resetScore && !!score,
			onClick: () =>
				openModal('ResetScore', {
					account: client,
					userId: AppService.getSelf().id,
					customerId: customerId,
					synch: !(contactsTotal || 0 > 1000)
				})
		},
		{
			id: 'files',
			icon: 'cloud-upload',
			title: T('clientCard.splitButton.files'),
			visible: () => client.userEditable && canCreateEntity.files && canCreateEntity.document,
			onClick: () => {
				if (hasReactUploadFileModal) {
					return openModal('UploadFileModal', { accountId: client.id });
				}
				Tools.$upModal.open('uploadFile', { accountId: client.id });
			}
		},
		{
			id: 'eSign',
			icon: 'edit',
			title: T('clientCard.splitButton.esign'),
			visible: () => canCreateEntity.esign && hasEsign,
			onClick: () => openEditEsign({ esign: { client: { id: client.id } } })
		},
		{
			id: 'target',
			icon: 'bullseye',
			title: T('clientCard.splitButton.target'),
			visible: () => canCreateEntity.target,
			onClick: () => openModal('EditClientTarget', { clientId: client.id, quotas: client.clientQuotas })
		},
		{
			id: 'support',
			icon: 'customer-support',
			title: T('clientCard.splitButton.support'),
			visible: () => canCreateEntity.support,
			onClick: () =>
				openModal('EditTicket', {
					client: {
						id: client.id,
						name: client.name,
						operationalAccount: client.operationalAccount,
						users: client.users,
						journeyStep: client.journeyStep
					}
				})
		},
		{
			id: 'subaccount',
			icon: 'subaccounts',
			title: T('clientCard.splitButton.subaccount'),
			visible: () =>
				!!client.active &&
				// For presto version atm
				(!hasSubaccountsV2 || hasSubaccountsFeature) &&
				!client.operationalAccount &&
				client.prospecting?.headquarters !== 0,
			onClick: () => {
				if (hasSubaccountsFeature) {
					return openDrawer('CreateRelation', { client, defaultView: CreateRelationView.Subaccount });
				}
				openDrawer('SubaccountDrawer', {
					client: {
						id: client.id,
						name: client.name,
						hasAccess: true
					},
					account: client,
					defaultView: SubaccountDrawerView.Add,
					includingSubaccountData: true
				});
			}
		},
		{
			id: 'projectPlan',
			icon: 'project',
			title: T('clientCard.splitButton.projectPlan'),
			visible: () => canCreateEntity.projectPlan,
			onClick: () => openDrawer('EditProjectPlan', { client })
		}
	];
};
